import { Schema, model, models, Model } from "mongoose";
import { NetworkSymbols } from "based-auth/networks";

export enum NotificationTypes {
  FOLLOW = "FOLLOW",
  UNFOLLOW = "UNFOLLOW",
  INVITE = "INVITE",
  GIVE_INVITE_ROLE = "GIVE_INVITE_ROLE",
  TRADING_FEE_EARNED = "TRADING_FEE_EARNED",
  TOKEN_LAUNCHED = "TOKEN_LAUNCHED",
}

export interface INotification extends Document {
  recipient: {
    network: NetworkSymbols;
    address: string;
  };
  actor: {
    network: NetworkSymbols;
    address: string;
    name?: string;
    image?: string;
  };
  type: NotificationTypes;
  read: boolean;
  data: Record<string, any>;
  formatMessage(): string;
  createdAt: Date;
  updatedAt: Date;
}

const notificationSchema = new Schema<INotification>(
  {
    recipient: {
      network: {
        type: String,
        required: true,
        enum: Object.values(NetworkSymbols),
      },
      address: { type: String, required: true },
    },
    actor: {
      network: {
        type: String,
        required: true,
        enum: Object.values(NetworkSymbols),
      },
      address: { type: String, required: true },
      name: String,
      image: String,
    },
    type: {
      type: String,
      required: true,
      enum: Object.values(NotificationTypes),
    },
    read: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Schema.Types.Mixed,
      default: {},
    },
  },
  {
    timestamps: true,
    toObject: {
      getters: true,
      versionKey: false,
      transform: (doc, ret: any) => {
        ret.id = ret._id.toString();
        delete ret.__v;
        delete ret._id;
      },
    },
  }
);
// Compound index for efficient querying
notificationSchema.index({ "recipient.chainId": 1, "recipient.address": 1 });
notificationSchema.index({ createdAt: -1 });
notificationSchema.index({ read: 1 });

// Add method to format notification message
notificationSchema.methods.formatMessage = function (this: INotification) {
  const actorName =
    this.actor.name ||
    `${this.actor.address.slice(0, 6)}...${this.actor.address.slice(-4)}`;

  switch (this.type) {
    case NotificationTypes.FOLLOW:
      return `${actorName} started following you`;
    case NotificationTypes.UNFOLLOW:
      return `${actorName} unfollowed you`;
    case NotificationTypes.INVITE:
      return `${actorName} invited you to join their meme`;
    case NotificationTypes.GIVE_INVITE_ROLE:
      return `${actorName} gave you the invite role`;
    case NotificationTypes.TRADING_FEE_EARNED:
      return `You earned $${this.data.amount} from trading fees!`;
    case NotificationTypes.TOKEN_LAUNCHED:
      return `${this.data.tokenName || "A token"} you invested in has been launched!`;
    default:
      return "New notification";
  }
};

export const NotificationModel: Model<INotification> =
  models?.Notification ||
  model<INotification>("Notification", notificationSchema);
